import i18n from "@/i18n";

const UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ["year", 365 * 24 * 3600],
  ["month", 30 * 24 * 3600],
  ["week", 7 * 24 * 3600],
  ["day", 24 * 3600],
  ["hour", 3600],
  ["minute", 60],
];

// Chrome ships no Uzbek date data (it prints "-2 h", "2026 M08 1"), so Uzbek is formatted by hand.
const isUz = () => i18n.language.startsWith("uz");
const UZ_UNITS: Partial<Record<Intl.RelativeTimeFormatUnit, string>> = {
  year: "yil",
  month: "oy",
  week: "hafta",
  day: "kun",
  hour: "soat",
  minute: "daqiqa",
};
const UZ_MONTHS = ["yan", "fev", "mar", "apr", "may", "iyn", "iyl", "avg", "sen", "okt", "noy", "dek"];

/** "3 days ago" in the current UI language. */
export function relativeTime(iso: string): string {
  const seconds = (new Date(iso).getTime() - Date.now()) / 1000;
  const rtf = new Intl.RelativeTimeFormat(i18n.language, { numeric: "auto" });
  for (const [unit, size] of UNITS) {
    if (Math.abs(seconds) < size) continue;
    const n = Math.round(seconds / size);
    if (isUz()) return n < 0 ? `${-n} ${UZ_UNITS[unit]} oldin` : `${n} ${UZ_UNITS[unit]}dan keyin`;
    return rtf.format(n, unit);
  }
  return isUz() ? "hozirgina" : rtf.format(0, "minute");
}

/** "29 Oct 2026, 16:08". */
export function fullDate(iso: string): string {
  const d = new Date(iso);
  if (isUz()) return `${mediumDate(d)}, ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  return d.toLocaleString(i18n.language, { dateStyle: "medium", timeStyle: "short" });
}

/** "29 Oct 2026" (Uzbek: "29-okt, 2026"). */
export function mediumDate(d: Date): string {
  if (isUz()) return `${d.getDate()}-${UZ_MONTHS[d.getMonth()]}, ${d.getFullYear()}`;
  return new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium" }).format(d);
}

/** "Oct" (or "Oct 2026" when `withYear`). */
export function shortMonth(d: Date, withYear = false): string {
  if (isUz()) return withYear ? `${UZ_MONTHS[d.getMonth()]} ${d.getFullYear()}` : UZ_MONTHS[d.getMonth()]!;
  return new Intl.DateTimeFormat(i18n.language, { month: "short", ...(withYear ? { year: "numeric" } : {}) }).format(d);
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? "?";
  const second = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "";
  return (first + second).toUpperCase();
}
